import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatItemDisplay } from "@/lib/orderItem";
import { Link } from "react-router-dom";
import { useSetSelectTheme } from "@/contexts/SelectThemeContext";
import { ChevronDown, Printer, Eye } from "lucide-react";
import OrderTimeline from "@/components/OrderTimeline";

const statusStyles = {
  pending_payment: "bg-bone text-stone border-stone",
  paid: "bg-oxblood text-bone border-oxblood",
  shipped: "bg-charcoal text-bone border-charcoal",
  delivered: "bg-white text-oxblood border-oxblood",
  cancelled: "bg-borderSubtle text-stone border-borderSubtle line-through",
  abandoned: "bg-borderSubtle text-stone border-borderSubtle",
};

const statusLabel = {
  pending_payment: "Awaiting payment",
  paid: "Paid",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  abandoned: "Abandoned",
};

export default function Orders() {
  // iter148s — inherit Select cream palette when the buyer's flow came from
  // the Select tier (same pattern as Contact page). Read once on mount.
  const [tierPref] = useState(() => {
    try { return (localStorage.getItem("bbk_last_tier") || "supply").toLowerCase(); }
    catch { return "supply"; }
  });
  useSetSelectTheme(tierPref === "select");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/orders");
      setOrders(data);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="orders-page">
      <p className="overline mb-4">Ledger</p>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-12">Past orders.</h1>

      {loading ? (
        <p className="overline">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="border border-borderSubtle bg-white p-12 text-center">
          <p className="text-stone mb-6">No orders yet.</p>
          <Link to="/shop" className="overline text-oxblood hover:text-charcoal">Browse the catalog →</Link>
        </div>
      ) : (
        <div className="space-y-6" data-testid="orders-list">
          {orders.map((o) => {
            const isOpen = expanded === o.id;
            return (
            <div key={o.id} className="border border-borderSubtle bg-white" data-testid={`order-${o.order_number}`}>
              <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-center p-6 md:p-8">
                <div>
                  <Link to={`/orders/${o.id}`} className="overline hover:text-oxblood transition-colors" data-testid={`order-link-${o.order_number}`}>
                    #{o.order_number}
                  </Link>
                  <p className="text-xs text-stone font-mono mt-1">{new Date(o.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                </div>
                <div className="text-sm text-charcoal">
                  {o.items.map((i, idx) => (
                    <span key={idx} className="mr-3 text-stone">{formatItemDisplay(i)} × {i.quantity}{idx < o.items.length - 1 ? "," : ""}</span>
                  ))}
                </div>
                <div className="flex items-center gap-4 flex-wrap justify-end">
                  <p className="font-mono text-lg text-oxblood">${o.total.toFixed(2)}</p>
                  <span className={`overline px-2 py-1 border ${statusStyles[o.status]}`} data-testid={`order-status-${o.order_number}`}>
                    {statusLabel[o.status]}
                  </span>
                  {o.status === "pending_payment" && (
                    <Link to={`/payment/${o.id}`} className="overline text-oxblood hover:text-charcoal" data-testid={`pay-link-${o.order_number}`}>Pay →</Link>
                  )}
                  <Link
                    to={`/orders/${o.id}`}
                    className="inline-flex items-center gap-1.5 overline text-charcoal hover:text-oxblood border border-borderSubtle hover:border-oxblood rounded-full px-3 py-1.5 transition-colors"
                    data-testid={`detail-${o.order_number}`}
                    aria-label={`Order detail for ${o.order_number}`}
                  >
                    <Eye size={12} strokeWidth={2}/> Details
                  </Link>
                  <Link
                    to={`/orders/${o.id}/slip`}
                    className="inline-flex items-center gap-1.5 overline text-charcoal hover:text-oxblood border border-borderSubtle hover:border-oxblood rounded-full px-3 py-1.5 transition-colors"
                    data-testid={`slip-${o.order_number}`}
                  >
                    <Printer size={12} strokeWidth={2}/> Slip
                  </Link>
                  <button
                    onClick={() => setExpanded(isOpen ? null : o.id)}
                    className="inline-flex items-center gap-1.5 overline text-charcoal hover:text-oxblood border border-borderSubtle hover:border-oxblood rounded-full px-3 py-1.5 transition-colors"
                    data-testid={`toggle-timeline-${o.order_number}`}
                    aria-expanded={isOpen}
                  >
                    Progress
                    <ChevronDown size={12} strokeWidth={2} className={`transition-transform ${isOpen ? "rotate-180" : ""}`}/>
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="px-6 md:px-8 pb-6 md:pb-8">
                  <OrderTimeline order={o} />
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
